import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ContactDestroyAction, ContactInitAction, ContactSubmitFormAction } from '@shared/store/actions/contact-actions';
import {
	LAYOUT_PAGE_TITLE,
	getContactDisableContractNo,
	getContactDisableNature,
	getContactDisableService,
	getContactDisableSubject,
	getContactHasError,
	getContactHasSuccess,
	getContactIsLoading,
	getContractData,
	getContractDataGroupId,
	getContractDataParkParkIdentificationNo,
	getContractDataParkParkName,
	getContractDataSectorId,
	getContractDataServiceAvailableServiceId,
	getContractDataServiceServiceDescription,
	getLayoutIsScope,
	getLayoutScopeId,
	getOrganizationById,
	getSessionClaimsAdmin,
	getSessionEmail,
	getSubscriptionDataSpaqContractNo,
	getSubscriptionDataSpaqFirstName,
	getSubscriptionDataSpaqLastName
} from '@spaq/core';

import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, firstValueFrom } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { getContractFilteredServices, getOrganizationServicesByGroupId, ServiceInfo } from '@spaq/utils';
import moment from 'moment-timezone';
import { BackendApiService } from '@shared/core';

@Component({
	selector: 'spaq-contact',
	template: `
		<p class="alert alert-success" *ngIf="success === true">
			Votre demande a été envoyée avec succès. Un courriel vous sera adressé dans les plus brefs délais.
		</p>
		<p class="alert alert-danger" *ngIf="success === false">
			Une erreur est survenue lors de l'envoi de votre demande. Veuillez réessayer ultérieurement.
		</p>
		<div *ngIf="selectedAction === 3 && success !== true" class="px-5">
			<p class="alert alert-info mt-4">
				Pour modifier un numéro de plaque d'immatriculation, ajouter, modifier ou retirer un véhicule, ou mettre à jour toute autre
				information personnelle, veuillez sélectionner « <a routerLink="/account/settings/subscription/edit">Paramètres</a> » dans
				le menu à gauche de l'écran.
			</p>
		</div>

		<div *ngIf="selectedAction === 1 && success !== true" class="px-5">
			<p class="alert alert-warning mt-4">
				Pour une demande de changement d'abonnement vers un autre stationnement, une autre organisation ou une autre catégorie
				d'usager, veuillez d'abord soumettre une demande de résiliation en cliquant sur "<a routerLink="/account/contract/cancel"
					>Transmettre une demande d'annulation</a
				>". Vous devrez soumettre une nouvelle demande d'abonnement le jour suivant la date de fin de votre abonnement actuel.
			</p>
			<h5 class="mt-5 mb-4">Sélectionnez l'abonnement souhaité</h5>
			<spaq-services-list
				*ngIf="!selectedService"
				id="step_4"
				(selectService)="onSelectService($event)"
				[services]="services$ | async"
				[isAdmin]="isAdmin$ | async"
				[showNote]="true"
				[scopeId]="scopeId$ | async"
			>
				<ng-content></ng-content>
			</spaq-services-list>
			<div *ngIf="selectedService">
				<p><b>Abonnement sélectionné:</b></p>
				{{ selectedService.serviceDescription }}
			</div>

			<spaq-button
				[disabled]="!selectedService || loading"
				(buttonClick)="clickSelectService()"
				[primary]="true"
				class="d-inline-block"
			>
				<span *ngIf="!loading">Transmettre la demande de changement d'abonnement</span>
				<span *ngIf="loading">Envoie en cours...</span>
			</spaq-button>
		</div>
		<div *ngIf="selectedAction === 2">
			<h5 class="mt-5 mb-1 mx-5">Remplissez votre demande de modifications</h5>
			<spaq-contact-form
				[pending]="isLoading$ | async"
				[values]="values$ | async"
				[hasSuccess]="hasSuccess$ | async"
				[hasError]="hasError$ | async"
				[disableService]="disableService$ | async"
				[disableNature]="disableNature$ | async"
				[disableSubject]="disableSubject$ | async"
				[disableContractNo]="disableContractNo$ | async"
				(formSuccess)="onSubmit($event)"
			></spaq-contact-form>
		</div>
	`,
	styles: []
})
export class ContactPageComponent implements OnInit, OnDestroy {
	success = null;
	selectedAction = 2;
	selectedService = null;
	loading = false;
	availableServiceId$ = this.store.pipe(select(getContractData));
	disableContractNo$ = this.store.pipe(select(getContactDisableContractNo));
	disableNature$ = this.store.pipe(select(getContactDisableNature));
	disableService$ = this.store.pipe(select(getContactDisableService));
	disableSubject$ = this.store.pipe(select(getContactDisableSubject));
	hasError$ = this.store.pipe(select(getContactHasError));
	hasSuccess$ = this.store.pipe(select(getContactHasSuccess));
	isLoading$ = this.store.pipe(select(getContactIsLoading));
	isAdmin$ = this.store.pipe(select(getSessionClaimsAdmin));
	isScope$ = this.store.pipe(select(getLayoutIsScope));
	scopeId$ = this.store.pipe(select(getLayoutScopeId));
	disabledServices$ = new BehaviorSubject([]);
	selectedParkId$ = this.store.pipe(select(getContractDataParkParkIdentificationNo));
	selectedGroupId$ = new BehaviorSubject(null);
	isSelectableGroup$ = this.scopeId$.pipe(
		switchMap(id =>
			this.store.pipe(select(getOrganizationById, id)).pipe(map((x: any) => Boolean(x && x.config && x.config.enableSelectableGroup)))
		)
	);
	isSectorIgnored$ = this.scopeId$.pipe(
		switchMap(id =>
			this.store
				.pipe(select(getOrganizationById, id))
				.pipe(map((x: any) => Boolean((x && x.config && x.config.ignoreSubscriptionSectorSelection) !== false)))
		)
	);

	organizationServices$ = combineLatest([this.selectedGroupId$, this.selectedParkId$]).pipe(
		switchMap(([g, p]) =>
			this.store.pipe(
				select(getOrganizationServicesByGroupId, g && g.groupCode),
				map(ss => (ss || []).filter(s => s.parkIdentificationNo === p))
			)
		)
	);
	selectedSectorId$ = new BehaviorSubject(null);
	services$ = combineLatest([
		this.isSelectableGroup$,
		this.isScope$,
		this.scopeId$,
		this.selectedSectorId$,
		this.organizationServices$,
		this.store.pipe(select(getContractFilteredServices)),
		this.isSectorIgnored$,
		this.store.pipe(select(getSessionClaimsAdmin)),
		this.disabledServices$
	]).pipe(
		map(([isSelectableGroup, _isScope, _scopeId, sector, services, allServices, isSectorIgnored, isAdmin, disabledServices]) => {
			if (!isSelectableGroup) {
				return allServices
					.filter(
						(s: any) =>
							moment(s.availableUntil).toDate().getTime() > moment().toDate().getTime() &&
							(isAdmin || !(s.meta && s.meta.vip))
					)
					.filter((s: any) => {
						if (!s.webEffectiveDate) {
							return true;
						}

						const now = moment();

						const effectiveDateStr = moment(s.webEffectiveDate).local().format('YYYY-MM-DD');
						const effectiveDateTime = moment(`${effectiveDateStr} ${s.webEffectiveHour}`, 'YYYY-MM-DD HH:mm:ss');

						if (now.isBefore(effectiveDateTime)) {
							return isAdmin;
						}

						const endDateStr = moment(s.webEndDate).local().format('YYYY-MM-DD');
						const endDateTime = moment(`${endDateStr} ${s.webEndHour}`, 'YYYY-MM-DD HH:mm:ss');

						if (now.isAfter(endDateTime)) {
							return isAdmin;
						}

						return true;
					})
					.map((s: any) => {
						if (s.webEffectiveDate) {
							if (moment(s.webEffectiveDate).toDate().getTime() > moment().toDate().getTime()) {
								s.availableLater = true;
							}
							if (s.webEndDate && moment(s.webEndDate).toDate().getTime() < moment().toDate().getTime()) {
								s.availableLater = false;
							}
							return s;
						}
						return s;
					})
					.sort((b, a) => a.price - b.price)
					.sort((b, a) => {
						return a.isSubscriptionAvailable === b.isSubscriptionAvailable ? 0 : a.isSubscriptionAvailable ? 1 : -1;
					})
					.map(service => {
						if (disabledServices.includes(service.availableServiceId)) {
							service.isSubscriptionAvailable = false;
						} else {
							service.isSubscriptionAvailable = true;
						}
						return service;
					});
			}
			if (isSectorIgnored) {
				return services
					.filter(
						(s: any) =>
							moment(s.availableUntil).toDate().getTime() > moment().toDate().getTime() &&
							(isAdmin || !(s.meta && s.meta.vip))
					)
					.filter((s: any) => {
						if (s.webEffectiveDate) {
							if (moment(s.webEffectiveDate).toDate().getTime() > moment().toDate().getTime()) {
								return isAdmin;
							}
							if (moment(s.webEndDate).toDate().getTime() < moment().toDate().getTime()) {
								return isAdmin;
							}
							return true;
						}
						return true;
					})
					.map((s: any) => {
						if (s.webEffectiveDate) {
							if (moment(s.webEffectiveDate).toDate().getTime() > moment().toDate().getTime()) {
								s.availableLater = true;
							}
							if (s.webEndDate && moment(s.webEndDate).toDate().getTime() < moment().toDate().getTime()) {
								s.availableLater = false;
							}
							return s;
						}
						return s;
					})
					.sort((b, a) => a.price - b.price)
					.sort((b, a) => {
						return a.isSubscriptionAvailable === b.isSubscriptionAvailable ? 0 : a.isSubscriptionAvailable ? 1 : -1;
					})
					.map(service => {
						if (disabledServices.includes(service.availableServiceId)) {
							service.isSubscriptionAvailable = false;
						}
						return service;
					});
			}
			return (services || [])
				.filter(
					(s: any) =>
						moment(s.availableUntil).toDate().getTime() > moment().toDate().getTime() && (isAdmin || !(s.meta && s.meta.vip))
				)
				.filter((s: any) => {
					if (s.webEffectiveDate) {
						if (moment(s.webEffectiveDate).toDate().getTime() > moment().toDate().getTime()) {
							return isAdmin;
						}
						if (moment(s.webEndDate).toDate().getTime() < moment().toDate().getTime()) {
							return isAdmin;
						}
						return true;
					}
					return true;
				})
				.map((s: any) => {
					if (s.webEffectiveDate) {
						if (moment(s.webEffectiveDate).toDate().getTime() > moment().toDate().getTime()) {
							s.availableLater = true;
						}
						if (s.webEndDate && moment(s.webEndDate).toDate().getTime() < moment().toDate().getTime()) {
							s.availableLater = false;
						}
						return s;
					}
					return s;
				})
				.sort((b, a) => b.serviceDescription.localeCompare(a.serviceDescription))
				.sort((b, a) => {
					return a.isSubscriptionAvailable === b.isSubscriptionAvailable ? 0 : a.isSubscriptionAvailable ? 1 : -1;
				})
				.filter(s => s.sectorId === (sector && sector.id))
				.map(service => {
					if (disabledServices.includes(service.availableServiceId)) {
						service.isSubscriptionAvailable = false;
					} else {
						service.isSubscriptionAvailable = true;
					}
					return service;
				});
		})
	);
	values$: any = combineLatest<any>(
		this.store.pipe(select(getSubscriptionDataSpaqContractNo)),
		this.store.pipe(select(getSessionEmail)),
		this.store.pipe(select(getContractDataServiceServiceDescription)),
		this.store.pipe(select(getContractDataServiceAvailableServiceId)),
		this.store.pipe(select(getSubscriptionDataSpaqFirstName)),
		this.store.pipe(select(getSubscriptionDataSpaqLastName)),
		this.store.pipe(select(getContractDataParkParkName))
	).pipe(
		mergeMap(([contractNo, email, serviceDescription, serviceId, firstName, lastName, parkName]) =>
			this.translate
				.get(['CONTACT.PAGES.FORM.NATURE_VALUE', 'CONTACT.PAGES.FORM.SUBJECT_VALUE'], { contractno: contractNo })
				.pipe(
					map(x => [contractNo, email, serviceDescription, serviceId, firstName, lastName, parkName, ...Object.values(x || [])])
				)
		),
		map(([contractNo, email, serviceDescription, serviceId, firstName, lastName, parkName, nature, subject]) => ({
			nature,
			currentDate: new Date().toISOString(),
			subject,
			contractNo,
			fullName: firstName + ' ' + lastName,
			serviceDescription: serviceDescription || '----',
			parkName: parkName || '----',
			serviceId: serviceId || '----',
			email,
			body: ''
		}))
	);

	constructor(
		private translate: TranslateService,
		private backendApi: BackendApiService,
		private store: Store<any>,
		@Inject(LAYOUT_PAGE_TITLE) layoutPageTitle: BehaviorSubject<string>
	) {
		layoutPageTitle.next('CONTACT.PAGES.FORM.MAIN_TITLE');
	}

	ngOnDestroy() {
		this.store.dispatch(new ContactDestroyAction());
	}

	ngOnInit() {
		this.store.dispatch(new ContactInitAction());

		combineLatest([
			this.store.pipe(select(getContractDataGroupId)),
			this.store.pipe(select(getContractDataSectorId)),
			this.store.pipe(select(getContractDataParkParkIdentificationNo))
		])
			.pipe(
				tap(([groupId, sectorId, _parkNo]: any) => {
					this.selectedGroupId$.next(groupId);
					this.selectedSectorId$.next(sectorId);
				})
			)
			.subscribe();
	}

	onSubmit($event) {
		this.store.dispatch(new ContactSubmitFormAction({ ...$event, host: window.location.hostname }));
	}

	onSelectService(service: ServiceInfo) {
		this.selectedService = service;
	}

	async clickSelectService(_availableServiceId = null) {
		combineLatest([this.availableServiceId$])
			.pipe(
				take(1),
				tap(async ([availableServiceId]) => {
					try {
						const serviceId = Object.values((availableServiceId && availableServiceId.subscriptionServices) || {});

						this.loading = true;
						await firstValueFrom(
							this.backendApi.callTransferService({
								availableServiceId: serviceId[0].serviceId,
								newAvailableServiceId: this.selectedService.availableServiceId,
								transfertEffectiveDate: this.selectedService.period.startDateRaw,
								transfertEndDate: this.selectedService.period.endDateRaw
							})
						);
						this.success = true;
						this.loading = false;
					} catch (e) {
						this.loading = false;
						this.success = false;
					} finally {
						this.loading = false;
					}
				})
			)
			.subscribe();
	}
}
